import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, XCircle, Clock, FileText, RefreshCw, RotateCcw, ChevronDown, Inbox } from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const API_BASE = '/api/compliance';

type EntityType = 'evidence' | 'policy' | 'capa' | 'vendor_assessment' | 'management_review' | 'scope_change' | 'internal_audit_finding';
type ApprovalStatus = 'pending' | 'in_review' | 'approved' | 'rejected' | 'cancelled' | 'escalated';
type Decision = 'approved' | 'rejected' | 'returned_for_rework';

interface ApprovalRequest {
  id: number;
  entity_type: EntityType;
  entity_id: number;
  entity_title?: string;
  status: ApprovalStatus;
  submitted_by: string;
  submitted_by_name?: string;
  submitted_at: string;
  current_step?: number;
  total_steps?: number;
  step_label?: string;
  decided_at?: string;
  decided_by?: string;
  decision?: Decision;
  comments?: string;
}

const statusColors: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800',
  in_review: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-700',
  escalated: 'bg-orange-100 text-orange-800',
  returned_for_rework: 'bg-yellow-100 text-yellow-800',
};

const entityTypeLabels: Record<EntityType, string> = {
  evidence: 'Evidence',
  policy: 'Policy',
  capa: 'CAPA',
  vendor_assessment: 'Vendor Assessment',
  management_review: 'Management Review',
  scope_change: 'Scope Change',
  internal_audit_finding: 'Audit Finding',
};

const entityTypeColors: Record<EntityType, string> = {
  evidence: 'bg-indigo-100 text-indigo-800',
  policy: 'bg-purple-100 text-purple-800',
  capa: 'bg-rose-100 text-rose-800',
  vendor_assessment: 'bg-teal-100 text-teal-800',
  management_review: 'bg-cyan-100 text-cyan-800',
  scope_change: 'bg-orange-100 text-orange-800',
  internal_audit_finding: 'bg-slate-100 text-slate-800',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const ApprovalQueue: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');
  const [pending, setPending] = useState<ApprovalRequest[]>([]);
  const [history, setHistory] = useState<ApprovalRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Decision modal state
  const [decisionModal, setDecisionModal] = useState<{ request: ApprovalRequest; type: Decision } | null>(null);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Filter state for history
  const [historyEntityFilter, setHistoryEntityFilter] = useState<string>('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<string>('');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/approvals/pending`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load approvals');
      const data = await res.json();
      const all: ApprovalRequest[] = Array.isArray(data) ? data : (data.approvals || data.pending || []);
      setPending(all.filter(a => a.status === 'pending' || a.status === 'in_review'));
      setHistory(all.filter(a => a.status !== 'pending' && a.status !== 'in_review'));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDecision = async () => {
    if (!decisionModal) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/approvals/${decisionModal.request.id}/decide`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ decision: decisionModal.type, comments }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to submit decision');
      }
      setDecisionModal(null);
      setComments('');
      loadData();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openDecisionModal = (request: ApprovalRequest, type: Decision) => {
    setDecisionModal({ request, type });
    setComments('');
  };

  const filteredHistory = history.filter(a => {
    if (historyEntityFilter && a.entity_type !== historyEntityFilter) return false;
    if (historyStatusFilter && a.status !== historyStatusFilter) return false;
    return true;
  });

  const decisionConfig: Record<Decision, { title: string; color: string; btnClass: string; icon: React.ReactNode }> = {
    approved: {
      title: 'Approve',
      color: 'text-green-700',
      btnClass: 'bg-emerald-600 hover:bg-emerald-700 text-white',
      icon: <CheckCircle2 size={20} />,
    },
    rejected: {
      title: 'Reject',
      color: 'text-red-700',
      btnClass: 'bg-red-600 hover:bg-red-700 text-white',
      icon: <XCircle size={20} />,
    },
    returned_for_rework: {
      title: 'Return for Rework',
      color: 'text-yellow-700',
      btnClass: 'bg-yellow-500 hover:bg-yellow-600 text-white',
      icon: <RotateCcw size={20} />,
    },
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Approval Queue</h1><PageInfoButton /></div>
          {pending.length > 0 && (
            <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-amber-100 text-amber-800">
              {pending.length} pending
            </span>
          )}
        </div>
        <button
          onClick={loadData}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-100">
        <nav className="flex gap-6">
          <button
            onClick={() => setActiveTab('pending')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'pending'
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Pending
            {pending.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                {pending.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            History
            {history.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                {history.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw size={24} className="animate-spin text-slate-400" />
          <span className="ml-2 text-slate-500">Loading approvals...</span>
        </div>
      ) : activeTab === 'pending' ? (
        /* Pending Tab */
        pending.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <Inbox size={32} className="text-emerald-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-1">No pending approvals</h3>
            <p className="text-sm text-slate-500">You are all caught up. New approval requests will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map(item => (
              <div
                key={item.id}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  {/* Left side: entity info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${entityTypeColors[item.entity_type]}`}>
                        {entityTypeLabels[item.entity_type]}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[item.status]}`}>
                        {item.status === 'in_review' ? 'In Review' : 'Pending'}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 truncate">
                      <FileText size={14} className="inline mr-1.5 text-slate-400" />
                      {item.entity_title || `${entityTypeLabels[item.entity_type]} #${item.entity_id}`}
                    </h3>
                    <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500">
                      <span>Submitted by <span className="font-medium text-slate-700">{item.submitted_by_name || item.submitted_by}</span></span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {timeAgo(item.submitted_at)}
                      </span>
                      {item.current_step && item.total_steps && (
                        <span className="text-slate-400">
                          Step {item.current_step} of {item.total_steps}
                          {item.step_label ? ` — ${item.step_label}` : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right side: action buttons */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => openDecisionModal(item, 'returned_for_rework')}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
                    >
                      <RotateCcw size={14} />
                      Rework
                    </button>
                    <button
                      onClick={() => openDecisionModal(item, 'rejected')}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <XCircle size={14} />
                      Reject
                    </button>
                    <button
                      onClick={() => openDecisionModal(item, 'approved')}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      <CheckCircle2 size={14} />
                      Approve
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* History Tab */
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <select
                value={historyEntityFilter}
                onChange={e => setHistoryEntityFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All types</option>
                {Object.entries(entityTypeLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            <div className="relative">
              <select
                value={historyStatusFilter}
                onChange={e => setHistoryStatusFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All statuses</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
                <option value="escalated">Escalated</option>
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {filteredHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
                <FileText size={32} className="text-slate-300" />
              </div>
              <h3 className="text-lg font-medium text-slate-700 mb-1">No approval history</h3>
              <p className="text-sm text-slate-500">Completed approvals will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredHistory.map(item => (
                <div
                  key={item.id}
                  className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${entityTypeColors[item.entity_type]}`}>
                          {entityTypeLabels[item.entity_type]}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[item.status] || 'bg-gray-100 text-gray-700'}`}>
                          {item.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900 truncate">
                        <FileText size={14} className="inline mr-1.5 text-slate-400" />
                        {item.entity_title || `${entityTypeLabels[item.entity_type]} #${item.entity_id}`}
                      </h3>
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500 flex-wrap">
                        <span>Submitted by <span className="font-medium text-slate-700">{item.submitted_by_name || item.submitted_by}</span></span>
                        <span>{formatDate(item.submitted_at)}</span>
                        {item.decided_at && (
                          <span>Decided {formatDate(item.decided_at)}</span>
                        )}
                      </div>
                      {item.comments && (
                        <p className="mt-2 text-xs text-slate-500 italic bg-slate-50 rounded-lg px-3 py-2">
                          "{item.comments}"
                        </p>
                      )}
                    </div>
                    <div className="shrink-0">
                      {item.status === 'approved' && <CheckCircle2 size={24} className="text-green-500" />}
                      {item.status === 'rejected' && <XCircle size={24} className="text-red-500" />}
                      {item.status === 'cancelled' && <XCircle size={24} className="text-gray-400" />}
                      {item.status === 'escalated' && <Clock size={24} className="text-orange-500" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Decision Modal */}
      {decisionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !submitting && setDecisionModal(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className={decisionConfig[decisionModal.type].color}>
                {decisionConfig[decisionModal.type].icon}
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                {decisionConfig[decisionModal.type].title}
              </h2>
            </div>

            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${entityTypeColors[decisionModal.request.entity_type]}`}>
                  {entityTypeLabels[decisionModal.request.entity_type]}
                </span>
              </div>
              <p className="text-sm font-medium text-slate-800">
                {decisionModal.request.entity_title || `${entityTypeLabels[decisionModal.request.entity_type]} #${decisionModal.request.entity_id}`}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Submitted by {decisionModal.request.submitted_by_name || decisionModal.request.submitted_by}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Comments {decisionModal.type === 'rejected' || decisionModal.type === 'returned_for_rework' ? '(required)' : '(optional)'}
              </label>
              <textarea
                value={comments}
                onChange={e => setComments(e.target.value)}
                rows={3}
                placeholder={
                  decisionModal.type === 'approved'
                    ? 'Add any comments...'
                    : 'Explain the reason for this decision...'
                }
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setDecisionModal(null); setComments(''); }}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDecision}
                disabled={
                  submitting ||
                  ((decisionModal.type === 'rejected' || decisionModal.type === 'returned_for_rework') && !comments.trim())
                }
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${decisionConfig[decisionModal.type].btnClass}`}
              >
                {submitting ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  decisionConfig[decisionModal.type].icon
                )}
                {decisionConfig[decisionModal.type].title}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalQueue;
